// The two hex-grid layers on the WebGL2 engine (R456, R501,
// ui-map-interface.md §4.3).
//
// The shipped map never draws the hex grid: territory and terrain render as
// smoothed organic shapes, and the grid under them is an implementation
// detail. These two layers put it back, on a key, for the owner and for a
// reviewer who has to read exactly which cells a faction holds.
//
// They are the port of `render/hexgrid.ts`, and they keep its two rules:
//
//   - **Static between owner changes.** Nothing here advances on a clock. A
//     rebuild writes the instances once and every later frame re-flushes
//     them, so a still map costs one `flush` a layer.
//   - **The rebuild is LAZY.** Both layers ship hidden, and the engine skips
//     `update` on an invisible layer (`layer.ts`), so a whole season of
//     flips costs one flag each and the reader who presses the key pays the
//     retrace once, on the first visible frame.
//
// The fill is one `hex` sprite per owned cell, tinted with the faction's own
// colour at `HEXGRID.fillAlpha` — this view has its own wash and does NOT
// read the territory tint. The PixiJS layer draws one path of every hex and
// one `fill()` per faction; an instanced sprite is the same idea on this
// engine, and the atlas cell is baked from `world.hexCorner`, so the drawn
// hex IS the map's hex. The border is one segment run per traced loop of
// `traceHexLoops` and nothing else: no Laplacian relax and no Chaikin,
// because the corners are the whole point of the view.

import { HEXGRID } from '../../gen/presentation';
import { ownedCellsByFaction } from '../../render/hexgrid-cells';
import { traceHexLoops } from '../../render/outlines';
import { cellCenter, factionColor } from '../../world';
import type { AtlasEntry } from '../gfx/atlas';
import type { DrawCtx, FrameInfo, Layer } from '../layer';
import type { BatchSource, QuadSink, SegmentSink } from './batches';

/** How many hexes one batch starts with. It grows with the claim. */
const FILL_CAPACITY = 4096;

/** How many border segments one batch starts with. */
const BORDER_CAPACITY = 4096;

/**
 * What the two layers share: the owners reference, the blocked mask and the
 * lazy dirty flag.
 *
 * The owners array is HELD and not copied, exactly as `render/hexgrid.ts`
 * holds it: `feed.ts` mutates the territory layer's own bytes in place on
 * every flip, so a layer that copied would draw the world of a season ago.
 */
abstract class HexLayer implements Layer {
  abstract readonly name: string;

  protected owners: Uint8Array | null = null;
  protected blocked: Uint8Array | null = null;
  private shown = false;
  private dirty = false;
  /** How many times the layer rebuilt. The tests read it. */
  rebuilds = 0;

  /**
   * Takes the live owners array and the blocked mask, and stales the
   * picture. The hello calls it; a flip calls `invalidate`.
   */
  setOwners(owners: Uint8Array, blocked: Uint8Array | null): void {
    this.owners = owners;
    this.blocked = blocked;
    this.dirty = true;
  }

  /**
   * Marks the picture stale. One flag, however many flips arrive, and a flip
   * while the layer is hidden costs exactly this write.
   */
  invalidate(): void { this.dirty = true; }

  update(_frame: FrameInfo): void {
    // The engine calls this on a visible layer only, so the hidden state is
    // the whole of the laziness — there is no second guard to keep in step.
    if (!this.dirty) return;
    this.dirty = false;
    this.rebuilds++;
    this.rebuild();
  }

  abstract draw(ctx: DrawCtx): void;

  setVisible(v: boolean): void { this.shown = v; }
  get visible(): boolean { return this.shown; }

  /** Writes the instances for the owners the layer holds. */
  protected abstract rebuild(): void;
}

/** The filled hexes, under the territory outline. */
export class HexFillLayer extends HexLayer {
  readonly name = 'hexfills';

  private readonly quads: QuadSink;
  private readonly cell: AtlasEntry;

  constructor(src: BatchSource) {
    super();
    this.quads = src.newQuads(FILL_CAPACITY);
    this.cell = src.sprite('hex');
  }

  /** How many hexes the last rebuild wrote. The tests read it. */
  get count(): number { return this.quads.count; }

  draw(ctx: DrawCtx): void { this.quads.flush(ctx.cam); }

  protected rebuild(): void {
    const q = this.quads;
    q.begin();
    const owners = this.owners;
    if (!owners) return;
    // Ascending index order per faction, rock dropped: a stale owners byte
    // under a ridge must not draw a hex over the rock (`hexgrid-cells.ts`).
    const cells = ownedCellsByFaction(owners, this.blocked);
    for (const [faction, list] of cells) {
      const color = factionColor(faction);
      for (let i = 0; i < list.length; i++) {
        const [cx, cy] = cellCenter(list[i]);
        q.sprite(cx, cy, 0, this.cell, color, HEXGRID.fillAlpha);
      }
    }
  }
}

/** The hex outlines, above the fills. */
export class HexBorderLayer extends HexLayer {
  readonly name = 'hexborders';

  private readonly segments: SegmentSink;
  /** The owners with the rock zeroed, which the trace reads. The layer holds
   *  the owners array of the territory layer, so it never writes to it. */
  private traced = new Uint8Array(0);

  constructor(src: BatchSource) {
    super();
    this.segments = src.newSegments(BORDER_CAPACITY);
  }

  /** How many segments the last rebuild wrote. The tests read it. */
  get count(): number { return this.segments.count; }

  draw(ctx: DrawCtx): void { this.segments.flush(ctx.cam); }

  protected rebuild(): void {
    const s = this.segments;
    s.begin();
    let owners = this.owners;
    if (!owners) return;
    // Rock is never owned, so a stale owners byte under a ridge must not draw
    // a border with no fill, as the fill view drops it (R3018).
    const blocked = this.blocked;
    if (blocked) {
      if (this.traced.length !== owners.length) this.traced = new Uint8Array(owners.length);
      const t = this.traced;
      for (let i = 0; i < owners.length; i++) t[i] = blocked[i] ? 0 : owners[i];
      owners = t;
    }
    // One faction at a time, so a run carries one colour. The trace returns
    // closed loops, holes included, so an enclave inside a claim draws its
    // own outline the way the PixiJS layer's `poly()` does.
    const seen = new Set<number>();
    for (let i = 0; i < owners.length; i++) {
      const f = owners[i];
      if (f === 0 || seen.has(f)) continue;
      seen.add(f);
      const color = factionColor(f);
      const loops = traceHexLoops(owners, f);
      for (const loop of loops) {
        for (let k = 0; k < loop.length; k++) {
          const a = loop[k];
          const b = loop[(k + 1) % loop.length];
          s.line(a[0], a[1], b[0], b[1],
            HEXGRID.borderWidth, color, HEXGRID.borderAlpha);
        }
      }
    }
  }
}
